import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { motion } from 'framer-motion';
import { ExternalLink, MapPin, Building2 } from 'lucide-react';
import { experiencesExtended, ExperienceExtended } from '../../data/experienceData';
import { useCursorContext } from '../../hooks/useCursorContext';

gsap.registerPlugin(ScrollTrigger);

const typeLabels: Record<ExperienceExtended['type'], string> = {
  fulltime: 'Festanstellung',
  freelance: 'Freelance',
  agency: 'Eigene Agentur',
  project: 'Projekt',
};

const typeColors: Record<ExperienceExtended['type'], string> = {
  fulltime: 'bg-blue-500/20 text-blue-400',
  freelance: 'bg-purple-500/20 text-purple-400',
  agency: 'bg-emerald-500/20 text-emerald-400',
  project: 'bg-amber-500/20 text-amber-400',
};

export function ExperienceTimeline() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { setCursorState } = useCursorContext();

  useEffect(() => {
    let ctx: gsap.Context;

    const timer = setTimeout(() => {
      ctx = gsap.context(() => {
        // Animate timeline line
        gsap.fromTo(
          '.timeline-line-main',
          { scaleY: 0 },
          {
            scaleY: 1,
            duration: 1.5,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: containerRef.current,
              start: 'top 70%',
            },
          }
        );

        // Animate cards
        const cards = containerRef.current?.querySelectorAll('.exp-card-full');
        if (cards) {
          cards.forEach((card, i) => {
            gsap.fromTo(
              card,
              { x: i % 2 === 0 ? -60 : 60, opacity: 0 },
              {
                x: 0,
                opacity: 1,
                duration: 0.8,
                ease: 'power2.out',
                scrollTrigger: {
                  trigger: card,
                  start: 'top 85%',
                },
              }
            );
          });
        }

        // Animate dots
        const dots = containerRef.current?.querySelectorAll('.timeline-dot-main');
        if (dots) {
          gsap.fromTo(
            dots,
            { scale: 0, opacity: 0 },
            {
              scale: 1,
              opacity: 1,
              duration: 0.4,
              stagger: 0.1,
              ease: 'back.out(2)',
              scrollTrigger: {
                trigger: containerRef.current,
                start: 'top 70%',
              },
            }
          );
        }

        ScrollTrigger.refresh();
      }, containerRef);
    }, 150);

    return () => {
      clearTimeout(timer);
      if (ctx) ctx.revert();
    };
  }, []);

  // Sortiere nach Startjahr (neueste zuerst)
  const sortedExperiences = [...experiencesExtended].sort((a, b) => b.startYear - a.startYear);

  return (
    <section ref={containerRef} className="py-16 px-8 md:px-12 lg:px-24">
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="mb-12">
          <span className="text-xs text-white/30 tracking-[0.3em] uppercase">
            Timeline
          </span>
          <h2 className="text-3xl md:text-4xl font-light text-white mt-4">
            Berufliche <span className="text-emerald-500">Stationen</span>
          </h2>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline line */}
          <div className="timeline-line-main absolute left-[7px] md:left-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-emerald-500 via-emerald-500/30 to-transparent origin-top md:-translate-x-[1px]" />

          {/* Experience entries */}
          <div className="space-y-16">
            {sortedExperiences.map((exp, index) => {
              const isLeft = index % 2 === 0;

              return (
                <div key={exp.id} className="relative">
                  {/* Timeline dot */}
                  <div
                    className={`timeline-dot-main absolute left-0 md:left-1/2 top-8 w-4 h-4 rounded-full z-10 md:-translate-x-1/2 border-2 ${
                      exp.current
                        ? 'bg-emerald-500 border-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.6)]'
                        : 'bg-[#0a0a0a] border-white/30'
                    }`}
                  >
                    {exp.current && (
                      <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-40" />
                    )}
                  </div>

                  {/* Year marker (on opposite side) */}
                  <div
                    className={`hidden md:block absolute top-7 text-sm text-white/30 font-mono ${
                      isLeft ? 'left-[calc(50%+24px)]' : 'right-[calc(50%+24px)] text-right'
                    }`}
                  >
                    {exp.startYear}
                    {exp.endYear && exp.endYear !== exp.startYear && ` – ${exp.endYear}`}
                    {exp.current && ' – Heute'}
                  </div>

                  {/* Card */}
                  <motion.div
                    className={`exp-card-full pl-12 md:pl-0 md:w-[calc(50%-48px)] ${
                      isLeft ? 'md:mr-auto' : 'md:ml-auto'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div
                      className="p-6 md:p-8 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-emerald-500/30 transition-all duration-300 group"
                      onMouseEnter={() => setCursorState('hovering')}
                      onMouseLeave={() => setCursorState('default')}
                    >
                      {/* Header */}
                      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Building2 className="w-5 h-5 text-white/30" />
                            <h3 className="text-xl md:text-2xl font-medium text-white group-hover:text-emerald-500 transition-colors">
                              {exp.company}
                            </h3>
                          </div>
                          <p className="text-emerald-500/80">{exp.role}</p>
                        </div>

                        {/* Badges */}
                        <div className="flex flex-wrap items-center gap-2">
                          {exp.current && (
                            <span className="px-2 py-1 text-[10px] bg-emerald-500/20 text-emerald-500 rounded tracking-wider uppercase font-medium">
                              Aktuell
                            </span>
                          )}
                          <span className={`px-2 py-1 text-[10px] rounded tracking-wider uppercase font-medium ${typeColors[exp.type]}`}>
                            {typeLabels[exp.type]}
                          </span>
                        </div>
                      </div>

                      {/* Meta */}
                      <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-white/40">
                        <span>{exp.period}</span>
                        {exp.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {exp.location}
                          </span>
                        )}
                        <span className="text-emerald-500/60">{exp.industry}</span>
                      </div>

                      {/* Description */}
                      <p className="text-white/60 mb-6 leading-relaxed">
                        {exp.longDescription || exp.description}
                      </p>

                      {/* Highlights */}
                      <div className="mb-6">
                        <h4 className="text-xs text-white/30 tracking-wider uppercase mb-3">
                          Highlights
                        </h4>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {exp.highlights.map((highlight, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-white/50">
                              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500/60 flex-shrink-0" />
                              {highlight}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Technologies */}
                      <div className="flex flex-wrap gap-2">
                        {exp.technologies.slice(0, 6).map((tech) => (
                          <span
                            key={tech}
                            className="px-3 py-1 text-xs text-white/40 bg-white/5 rounded-full"
                          >
                            {tech}
                          </span>
                        ))}
                        {exp.technologies.length > 6 && (
                          <span className="px-3 py-1 text-xs text-white/30">
                            +{exp.technologies.length - 6}
                          </span>
                        )}
                      </div>

                      {/* Link to project (if exists) */}
                      {exp.url && (
                        <a
                          href={`https://${exp.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 mt-6 text-sm text-emerald-500 hover:text-emerald-400 transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          {exp.url}
                        </a>
                      )}
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-20 text-center">
          <p className="text-white/40 mb-6">
            Interessiert an einer Zusammenarbeit?
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-emerald-500 text-black rounded-full font-medium hover:bg-emerald-400 transition-colors"
            onMouseEnter={() => setCursorState('hovering')}
            onMouseLeave={() => setCursorState('default')}
          >
            Kontakt aufnehmen
          </Link>
        </div>
      </div>
    </section>
  );
}
